"use client";

import { useEffect, useState } from "react";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { Phone, X, CheckCircle, AlertCircle, ShoppingCart, MapPin, Globe, CreditCard, Tag, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";

interface AreaCodeRecommendation {
  areaCode: string;
  state: string;
  city?: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  estimatedCost: number;
  reason: string;
  frequency: number;
  lastCallDate: string;
  potentialMatches: number;
}

interface Lead {
  id: string;
  phone?: string;
  name: string;
}

interface BulkAreaCodeRecommendationNotificationProps {
  leads: Lead[]; // All selected leads
  userAreaCodes: string[]; // Area codes user already owns
  onPurchaseSuccess?: (purchasedAreaCodes: string[]) => void;
  onDismiss?: () => void;
}

export function BulkAreaCodeRecommendationNotification({
  leads,
  userAreaCodes,
  onPurchaseSuccess,
  onDismiss
}: BulkAreaCodeRecommendationNotificationProps) {
  // TEMPORARILY DISABLED - Remove this line to re-enable
  return null;

  const { toast } = useToast();
  const [missingAreaCodes, setMissingAreaCodes] = useState<{
    areaCode: string;
    state: string;
    leadCount: number;
    historicalCallCount: number;
    estimatedCost: number;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    reason: string;
    isAvailableInTwilio: boolean;
    popularity: number;
    closestAlternative?: {
      areaCode: string;
      state: string;
      distance: number;
      reason: string;
    };
  }[]>([]);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<Set<string>>(new Set());
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [recommendations, setRecommendations] = useState<AreaCodeRecommendation[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoValidation, setPromoValidation] = useState<{
    valid: boolean;
    discount: number; // percentage (e.g., 100 for 100% off)
    message: string;
    promoCodeId?: string;
  } | null>(null);

  // Analyze leads when component mounts or leads change
  // Also run analysis even with 0 leads to show HIGH priority area codes from historical data
  useEffect(() => {
    if (isDismissed) return;

    analyzeLeads();
  }, [leads, userAreaCodes, isDismissed]);

  const extractAreaCode = (phoneNumber: string): string | null => {
    if (!phoneNumber) return null;

    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.substring(1, 4);
    } else if (digits.length === 10) {
      return digits.substring(0, 3);
    }

    return null;
  };

  const analyzeLeads = async () => {
    try {
      // Extract all phone numbers from leads
      const leadPhoneNumbers = leads
        .map(lead => lead.phone)
        .filter((phone): phone is string => !!phone);

      // Don't return early - even with 0 leads, we want to check for HIGH priority
      // area codes from historical call data

      // Call new backend endpoint to analyze leads
      const response = await handleApiCall<{
        totalLeads: number;
        totalAreaCodes: number;
        missingAreaCodes: Array<{
          areaCode: string;
          state: string;
          leadCount: number;
          historicalCallCount: number;
          estimatedCost: number;
          priority: 'HIGH' | 'MEDIUM' | 'LOW';
          reason: string;
          isAvailableInTwilio: boolean;
          closestAlternative?: {
            areaCode: string;
            state: string;
            distance: number;
            reason: string;
          };
        }>;
        userStrategy: 'exact' | 'metropolitan' | 'nearest';
        strategyExplanation: string;
      }>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.communication.analyzeLeadsAreaCodes),
          body: {
            leadPhoneNumbers,
            aiMatchingEnabled: true,
          },
          auth: true,
        });
      });

      if (!response || !response.missingAreaCodes) {
        return;
      }

      // Filter to only show area codes available in Twilio (or with alternatives)
      const availableMissing = response.missingAreaCodes.filter(
        ac => ac.isAvailableInTwilio || ac.closestAlternative
      );

      if (availableMissing.length === 0) {
        // User already has all needed area codes
        return;
      }

      // Transform backend response to component format
      const enrichedMissing = availableMissing.map(ac => ({
        areaCode: ac.isAvailableInTwilio ? ac.areaCode : ac.closestAlternative!.areaCode,
        state: ac.isAvailableInTwilio ? ac.state : ac.closestAlternative!.state,
        leadCount: ac.leadCount,
        estimatedCost: ac.estimatedCost,
        priority: ac.priority,
        reason: ac.reason,
        isAlternative: !ac.isAvailableInTwilio,
        originalAreaCode: !ac.isAvailableInTwilio ? ac.areaCode : undefined,
      }));

      // Sort by priority: HIGH first, then MEDIUM, then LOW; secondary sort by leadCount
      const priorityOrder: Record<string, number> = { 'HIGH': 0, 'MEDIUM': 1, 'LOW': 2 };
      enrichedMissing.sort((a, b) => {
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return b.leadCount - a.leadCount;
      });

      setMissingAreaCodes(enrichedMissing as any);

      // Store recommendations for reference
      setRecommendations(availableMissing.map(ac => ({
        areaCode: ac.isAvailableInTwilio ? ac.areaCode : ac.closestAlternative!.areaCode,
        state: ac.isAvailableInTwilio ? ac.state : ac.closestAlternative!.state,
        priority: ac.priority,
        estimatedCost: ac.estimatedCost,
        reason: ac.reason,
        frequency: ac.leadCount,
        lastCallDate: new Date().toISOString(),
        potentialMatches: ac.leadCount,
      })));

      // Auto-select all HIGH priority recommendations
      setSelectedAreaCodes(prev => {
        const autoSelect = new Set(prev);
        enrichedMissing.forEach(({ areaCode, priority }) => {
          if (priority === 'HIGH' && !prev.has(areaCode)) {
            autoSelect.add(areaCode);
          }
        });
        return autoSelect;
      });

      setIsVisible(true);
    } catch (error) {
      // Show user-friendly error toast
      toast({
        title: "Area Code Check Failed",
        description: "Unable to check area code availability. Please try again later or contact support.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleToggleAreaCode = (areaCode: string) => {
    setSelectedAreaCodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(areaCode)) {
        newSet.delete(areaCode);
      } else {
        newSet.add(areaCode);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedAreaCodes(new Set(missingAreaCodes.map(m => m.areaCode)));
  };

  const handleDeselectAll = () => {
    setSelectedAreaCodes(new Set());
  };

  const handlePurchaseClick = () => {
    if (selectedAreaCodes.size === 0) {
      toast({
        title: "No Area Codes Selected",
        description: "Please select at least one area code to purchase.",
        variant: "destructive",
      });
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  // Validate promo code with Stripe
  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoValidation(null);
      return;
    }

    setIsValidatingPromo(true);
    try {
      const response = await handleApiCall<{
        valid: boolean;
        discount: number;
        message: string;
        promoCodeId?: string;
        durationInMonths?: number;
      }>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.areaCodes.validatePromoCode),
          body: { promoCode: promoCode.trim().toUpperCase() },
          auth: true,
        });
      });

      if (response) {
        setPromoValidation({
          valid: response.valid,
          discount: response.discount || 0,
          message: response.message,
          promoCodeId: response.promoCodeId,
        });

        if (response.valid) {
          toast({
            title: "Promo Code Applied! 🎉",
            description: response.message,
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      setPromoValidation({
        valid: false,
        discount: 0,
        message: error.message || 'Invalid promo code',
      });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleConfirmPurchase = async () => {
    // Native app: direct card-on-file purchase is a store violation — the
    // component is currently disabled, but if it ever comes back this stays.
    if (!allowCheckout("area-code")) return;

    setShowConfirmDialog(false);
    setIsPurchasing(true);

    try {
      // Purchase each selected area code
      const purchasePromises = Array.from(selectedAreaCodes).map(areaCode => {
        const missing = missingAreaCodes.find(m => m.areaCode === areaCode);
        return handleApiCall(async () => {
          return await api.post({
            url: getApiUrl(endpoint.areaCodes.autoPurchase),
            body: {
              areaCode,
              state: missing?.state || 'Unknown',
              reason: `Bulk purchase - ${missing?.leadCount || 0} leads need this area code`,
              // Include promo code if validated
              ...(promoValidation?.valid && promoValidation?.promoCodeId && {
                promoCode: promoCode.trim().toUpperCase(),
                promoCodeId: promoValidation.promoCodeId,
              }),
            },
            auth: true,
          });
        });
      });

      // Execute all purchases in parallel
      const results = await Promise.allSettled(purchasePromises);

      // Count successes and failures, and collect error messages
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failedResults = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      const failed = failedResults.length;

      // Check if any failures require checkout redirect (402 Payment Required)
      const requiresCheckout = failedResults.some(r => {
        try {
          const errorData = JSON.parse(r.reason?.message || '{}');
          return errorData.requiresCheckout === true;
        } catch {
          return false;
        }
      });

      if (requiresCheckout) {
        // Redirect to checkout for the first selected area code
        const firstAreaCode = Array.from(selectedAreaCodes)[0];
        const missing = missingAreaCodes.find(m => m.areaCode === firstAreaCode);

        toast({
          title: "Redirecting to Checkout",
          description: "No payment method on file. Setting up secure checkout...",
          duration: 3000,
        });

        // Call the checkout endpoint to get Stripe checkout URL
        const checkoutResponse = await handleApiCall(async () => {
          return await api.post({
            url: getApiUrl(endpoint.areaCodes.checkout),
            body: {
              areaCode: firstAreaCode,
              state: missing?.state || 'Unknown',
              // Include promo code if validated
              ...(promoValidation?.valid && promoValidation?.promoCodeId && {
                promoCodeId: promoValidation.promoCodeId,
              }),
            },
            auth: true,
          });
        });

        if (checkoutResponse?.data?.checkoutUrl) {
          if (!allowCheckout("area-code")) return;
          window.location.href = checkoutResponse.data.checkoutUrl;
          return;
        }
      }

      // Extract unique error messages from failed purchases
      const errorMessages = [...new Set(failedResults.map(r => {
        const reason = r.reason;
        // Try to parse JSON error message
        try {
          const parsed = JSON.parse(reason?.message || '{}');
          return parsed.message || reason?.message || reason?.toString() || 'Unknown error';
        } catch {
          return reason?.message || reason?.toString() || 'Unknown error';
        }
      }))];

      if (successful > 0) {
        toast({
          title: `${successful} Area Code${successful > 1 ? 's' : ''} Purchased! ✅`,
          description: `You can now call leads with perfect local matches. ${failed > 0 ? `${failed} failed - ${errorMessages[0] || 'check your payment method.'}` : ''}`,
          duration: 8000,
        });

        // Hide notification
        setIsVisible(false);

        // Call success callback
        if (onPurchaseSuccess) {
          onPurchaseSuccess(Array.from(selectedAreaCodes));
        }
      } else {
        // All purchases failed - show the actual error message
        const errorMessage = errorMessages.length > 0
          ? errorMessages[0]
          : 'Failed to purchase area codes. Please check your payment method and try again.';
        throw new Error(errorMessage);
      }
    } catch (error: any) {

      toast({
        title: "Purchase Failed",
        description: error.message || 'Failed to purchase area codes. Please check your payment method and try again.',
        variant: "destructive",
        duration: 6000,
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);

    if (onDismiss) {
      onDismiss();
    }
  };

  // Calculate total cost
  const totalCost = Array.from(selectedAreaCodes).reduce((sum, areaCode) => {
    const missing = missingAreaCodes.find(m => m.areaCode === areaCode);
    return sum + (missing?.estimatedCost || 2.0);
  }, 0);

  // Calculate total leads covered
  const totalLeads = Array.from(selectedAreaCodes).reduce((sum, areaCode) => {
    const missing = missingAreaCodes.find(m => m.areaCode === areaCode);
    return sum + (missing?.leadCount || 0);
  }, 0);

  // Calculate statistics
  const highPriorityCount = missingAreaCodes.filter(m => m.priority === 'HIGH').length;
  const totalAreaCodesOwned = userAreaCodes.length;

  // Coverage calculation: Total area codes in US is approximately 335
  // Coverage = (owned area codes / 335) * 100
  const TOTAL_US_AREA_CODES = 335;
  const coveragePercentage = ((totalAreaCodesOwned / TOTAL_US_AREA_CODES) * 100).toFixed(1);

  // Don't render if not visible or no missing area codes
  if (!isVisible || missingAreaCodes.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 right-0 sm:right-4 z-50 w-full sm:w-[500px] animate-in slide-in-from-bottom-5 mb-0">
      <Card className="border-2 border-orange-500 shadow-2xl bg-gradient-to-br from-orange-50 to-white rounded-none sm:rounded-tl-lg sm:rounded-tr-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-600" />
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Missing Area Codes Detected
                  {highPriorityCount > 0 && (
                    <Badge variant="destructive">
                      {highPriorityCount} HIGH PRIORITY
                    </Badge>
                  )}
                </CardTitle>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <div>{missingAreaCodes.length} area code{missingAreaCodes.length > 1 ? 's' : ''} needed for your {leads.length} selected lead{leads.length > 1 ? 's' : ''}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-blue-600 font-medium flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {totalAreaCodesOwned} area codes owned
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {coveragePercentage}% US coverage
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="h-6 w-6 p-0"
                disabled={isPurchasing}
                title={isMinimized ? "Expand" : "Minimize"}
              >
                {isMinimized ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="h-6 w-6 p-0"
                disabled={isPurchasing}
                title="Dismiss permanently"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {!isMinimized && (
          <CardContent className="space-y-3">
            {/* Select All / Deselect All */}
          {!showConfirmDialog && (
            <>
            <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Select area codes to purchase:</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className="h-7 text-xs"
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeselectAll}
                className="h-7 text-xs"
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Missing Area Codes List */}
          <ScrollArea className="h-[200px] border rounded-lg p-2">
            <div className="space-y-2">
              {missingAreaCodes.map(({ areaCode, state, leadCount, estimatedCost, priority, reason, isAlternative, originalAreaCode }: any) => {
                const isSelected = selectedAreaCodes.has(areaCode);

                // Determine badge variant and color based on priority
                let priorityBadge = null;
                if (priority === 'HIGH') {
                  priorityBadge = (
                    <Badge variant="destructive" className="text-xs h-5">
                      HIGH
                    </Badge>
                  );
                } else if (priority === 'MEDIUM') {
                  priorityBadge = (
                    <Badge className="text-xs h-5 bg-orange-500 text-white hover:bg-orange-600">
                      MEDIUM
                    </Badge>
                  );
                } else if (priority === 'LOW') {
                  priorityBadge = (
                    <Badge variant="secondary" className="text-xs h-5">
                      LOW
                    </Badge>
                  );
                }

                return (
                  <div
                    key={areaCode}
                    className={`flex flex-col p-2 rounded border ${
                      isSelected ? 'bg-orange-50 border-orange-300' : 'bg-white'
                    } hover:bg-orange-50 cursor-pointer transition-colors`}
                    onClick={() => handleToggleAreaCode(areaCode)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleAreaCode(areaCode)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{areaCode} - {state}</span>
                            {priorityBadge}
                            {isAlternative && (
                              <Badge variant="secondary" className="text-xs h-5">
                                ALTERNATIVE
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {leadCount} lead{leadCount > 1 ? 's' : ''} need {isAlternative ? `${originalAreaCode} (using ${areaCode})` : 'this area code'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-sm">${estimatedCost % 1 === 0 ? estimatedCost : estimatedCost.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">/month</div>
                        </div>
                      </div>
                    </div>
                    {isAlternative && (
                      <div className="mt-1 ml-9 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 flex items-center gap-2">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        <span>{originalAreaCode} not available. Recommended closest alternative: {areaCode}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Summary */}
          <div className="bg-orange-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span>Selected area codes:</span>
              <span className="font-semibold">{selectedAreaCodes.size}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Leads covered:</span>
              <span className="font-semibold">{totalLeads}</span>
            </div>
            <div className="flex justify-between text-sm font-medium">
              <span>Total monthly cost:</span>
              <span className="text-orange-600 text-lg">${totalCost % 1 === 0 ? totalCost : totalCost.toFixed(2)}/mo</span>
            </div>
          </div>
          </>
          )}

          {/* Action Buttons or Confirmation View */}
          {showConfirmDialog ? (
            <div className="space-y-3">
              {/* Confirm Header */}
              <div className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle className="h-4 w-4 text-orange-600" />
                Confirm Area Code Purchase
              </div>

              {/* Benefits Section - Compact */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                <h4 className="font-semibold text-green-900 text-xs mb-1.5 flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  Why Local Numbers Increase Close Rates
                </h4>
                <ul className="space-y-0.5 text-xs text-green-900">
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 font-bold">•</span>
                    <span><strong>300% higher answer rate</strong> - Prospects answer local calls 3x more often</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 font-bold">•</span>
                    <span><strong>Trust & familiarity</strong> - Local presence builds instant credibility</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 font-bold">•</span>
                    <span><strong>Spam filters avoid local</strong> - 78% fewer spam blocks on local numbers</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 font-bold">•</span>
                    <span><strong>Improved conversion</strong> - Agents using local numbers close 2.5x more deals</span>
                  </li>
                </ul>
              </div>

              {/* Promo Code Input */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Promo code (optional)"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        // Clear validation when input changes
                        if (promoValidation) setPromoValidation(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleValidatePromoCode();
                        }
                      }}
                      className="pl-8 h-8 text-xs uppercase"
                      disabled={isPurchasing || isValidatingPromo}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleValidatePromoCode}
                    disabled={!promoCode.trim() || isPurchasing || isValidatingPromo}
                    className="h-8 px-3 text-xs"
                  >
                    {isValidatingPromo ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
                {promoValidation && (
                  <div className={`text-xs px-2 py-1.5 rounded flex items-center gap-1.5 ${
                    promoValidation?.valid
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {promoValidation?.valid ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {promoValidation?.message}
                  </div>
                )}
              </div>

              {/* Purchase Summary - Compact */}
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Area codes selected:</span>
                  <span className="font-semibold">{selectedAreaCodes.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Leads you can now reach:</span>
                  <span className="font-semibold text-green-600">{totalLeads} leads</span>
                </div>
                {promoValidation?.valid && (promoValidation?.discount ?? 0) > 0 && (
                  <>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Original price:</span>
                      <span className="line-through">${totalCost % 1 === 0 ? totalCost : totalCost.toFixed(2)}/mo</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({promoValidation?.discount ?? 0}% off):</span>
                      <span>-${((totalCost * (promoValidation?.discount ?? 0)) / 100).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {promoValidation?.valid && promoValidation?.discount === 100 ? 'First month:' : 'Monthly investment:'}
                  </span>
                  <span className={`font-semibold ${promoValidation?.valid && (promoValidation?.discount ?? 0) > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {promoValidation?.valid && (promoValidation?.discount ?? 0) > 0 ? (
                      <>
                        ${((totalCost * (100 - (promoValidation?.discount ?? 0))) / 100).toFixed(2)}
                        {promoValidation?.discount === 100 && <span className="ml-1 text-xs">(FREE!)</span>}
                      </>
                    ) : (
                      `$${totalCost % 1 === 0 ? totalCost : totalCost.toFixed(2)}/mo`
                    )}
                  </span>
                </div>
                {promoValidation?.valid && promoValidation?.discount === 100 && (
                  <div className="text-xs text-muted-foreground italic">
                    Then ${totalCost % 1 === 0 ? totalCost : totalCost.toFixed(2)}/mo after first month
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleConfirmPurchase}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  size="sm"
                  disabled={isPurchasing}
                >
                  {isPurchasing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Yes, Purchase Now
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setShowConfirmDialog(false)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  disabled={isPurchasing}
                >
                  Back
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                <CreditCard className="h-3 w-3" />
                Charged to your existing payment method • Cancel anytime
              </p>
            </div>
          ) : (
            <>
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handlePurchaseClick}
                  disabled={isPurchasing || selectedAreaCodes.size === 0}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  size="sm"
                >
                  {isPurchasing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Purchasing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Purchase {selectedAreaCodes.size} Area Code{selectedAreaCodes.size > 1 ? 's' : ''}
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleDismiss}
                  disabled={isPurchasing}
                  variant="outline"
                  size="sm"
                  title="Hide this notification permanently"
                >
                  Dismiss
                </Button>
              </div>

              {/* Info Footer */}
              <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                <CreditCard className="h-3 w-3" />
                All charges go to your existing payment method • Instant provisioning
              </p>
            </>
          )}
        </CardContent>
        )}
      </Card>
    </div>
  );
}
